import { esPosterior } from '@/lib/dates';
import type { ConversacionResumen } from '@/types/conversations';

/**
 * Avisos de mensajes nuevos del inbox (funciones PURAS: se prueban en el smoke sin navegador).
 *
 * **Qué resuelve**: hasta ahora, si el asesor estaba en Productos o en Órdenes no había NADA en el
 * panel que le dijera que un cliente acababa de escribir; había que entrar a Conversaciones y
 * mirar la bandeja. Estas funciones convierten «la primera página de la bandeja + lo que el usuario
 * ya vio» en el número que pintan la burbuja del header, la del menú lateral, el aviso DENTRO de la
 * bandeja y el punto azul de la fila.
 *
 * **De dónde sale «hay un mensaje nuevo»** (el backend NO tiene columna de «no leído»: ver
 * `docs/INBOX.md` §*Sin estado de “no leído”*, así que el estado es del navegador):
 *  - se mira el ÚLTIMO mensaje de la conversación: si es del CLIENTE (`direccion === 'IN'`) y su
 *    `createdAt` es posterior al instante en que esa conversación se dio por vista, hay algo por
 *    leer. Si el último mensaje es nuestro (`OUT`), no hay nada nuevo: el negocio ya contestó.
 *  - «visto» se guarda en `stores/inbox-alerts-store.ts` (localStorage, por negocio y por usuario).
 *
 * **NO hay aviso flotante**: los mensajes llegan todo el rato y tapaban la pantalla de trabajo
 * (decisión del 25-sep-2026). El detalle de quién escribió se ve en la propia bandeja.
 *
 * **Límites, a propósito y visibles en la pantalla**:
 *  - la burbuja cuenta CONVERSACIONES con mensajes sin ver, no mensajes: el listado del backend no
 *    dice cuántos mensajes llegaron desde la última visita (solo `mensajesTotal`);
 *  - solo se mira la PRIMERA página de la bandeja (`CONVERSACIONES_POR_PAGINA` = 30). Un mensaje
 *    sube su conversación al primer puesto por actividad, así que para que una quede fuera de esa
 *    página tendrían que haber escrito otras 30 conversaciones más recientes;
 *  - las conversaciones que «esperan a una persona» (`porAtender`) NO dependen de lo visto: salen
 *    de `requiereAtencionHumana` (el bot pausado), que es un dato del backend.
 */

/** Un aviso: la conversación y el instante del mensaje que todavía no se vio. */
export interface AlertaInbox {
  conversacion: ConversacionResumen;
  /** `createdAt` del último mensaje ENTRANTE sin ver (lo que se compara con lo visto). */
  nuevoEn: string;
}

/** Lo que pinta el shell: las burbujas del header/menú y el aviso de la bandeja. */
export interface InboxAlertas {
  /** Conversaciones con un mensaje entrante posterior a la última visita, la más reciente primero. */
  nuevas: AlertaInbox[];
  /** Conversaciones con el bot pausado: las está esperando una persona (handoff). */
  porAtender: number;
}

/** Sin novedades: lo que devuelve el hook mientras carga, con error o sin negocio activo. */
export const ALERTAS_VACIAS: InboxAlertas = { nuevas: [], porAtender: 0 };

/** Textos de los avisos (constantes para que el smoke los compruebe sin adivinar el literal). */
export const alertasTexto = {
  /** Acceso al inbox cuando NO hay nada nuevo. */
  accesoInbox: 'Ver las conversaciones',
  /** Botón del aviso de la bandeja: da por vista la bandeja entera sin abrir nada. */
  marcarVisto: 'Marcar como visto',
} as const;

/** Tope de la burbuja: a partir de ahí se pinta «9+» (un número de tres cifras no cabe). */
export const ALERTAS_MAX_EN_BURBUJA = 9;

/**
 * Instante del último mensaje de la conversación (venga del cliente o del negocio), o `null` si
 * todavía no hay ninguno. Es el valor con el que se marca la conversación como vista.
 */
export function ultimoMensajeEn(
  conversacion: Pick<ConversacionResumen, 'ultimoMensaje' | 'lastMessageAt'>,
): string | null {
  if (conversacion.ultimoMensaje?.createdAt) return conversacion.ultimoMensaje.createdAt;
  return conversacion.lastMessageAt ?? null;
}

/**
 * ¿Esta conversación tiene un mensaje del cliente sin ver?
 *
 * `vistoEn` es el instante hasta el que el usuario la dio por leída. Sin marca —una conversación
 * creada después de la siembra— cuenta como no vista; y si el último mensaje es nuestro no hay nada
 * que leer, aunque nunca se haya abierto el chat.
 */
export function mensajeSinVer(
  conversacion: Pick<ConversacionResumen, 'ultimoMensaje'>,
  vistoEn: string | undefined,
): boolean {
  const ultimo = conversacion.ultimoMensaje;
  if (!ultimo || ultimo.direccion !== 'IN') return false;
  if (!vistoEn) return true;

  return esPosterior(ultimo.createdAt, vistoEn);
}

/**
 * Las novedades de una página de la bandeja.
 *
 * `siembraHecha` es el «ya sé lo que había»: sin ella no se avisa de nada, porque el día que se
 * estrena la función TODA la bandeja es «nueva» para el navegador y la burbuja diría «28» con
 * conversaciones de meses atrás (ver `entradasDeVistos` y el hook).
 */
export function calcularAlertas(
  conversaciones: ConversacionResumen[],
  vistos: Record<string, string>,
  siembraHecha: boolean,
): InboxAlertas {
  const porAtender = conversaciones.filter(
    (conversacion) => conversacion.requiereAtencionHumana,
  ).length;

  if (!siembraHecha) return { nuevas: [], porAtender };

  const nuevas = conversaciones.flatMap((conversacion) => {
    const ultimo = conversacion.ultimoMensaje;
    if (!ultimo || !mensajeSinVer(conversacion, vistos[conversacion.id])) return [];

    return [{ conversacion, nuevoEn: ultimo.createdAt }];
  });

  // La más reciente primero: es el orden de la bandeja y lo que el aviso enseña arriba.
  nuevas.sort((a, b) => (esPosterior(b.nuevoEn, a.nuevoEn) ? 1 : -1));

  return { nuevas, porAtender };
}

/**
 * Conversación → instante de su último mensaje: lo que se guarda al sembrar la bandeja o al pulsar
 * «Marcar como visto». Las conversaciones sin mensajes se omiten (no hay nada que marcar).
 */
export function entradasDeVistos(conversaciones: ConversacionResumen[]): Record<string, string> {
  const entradas: Record<string, string> = {};

  conversaciones.forEach((conversacion) => {
    const vistoEn = ultimoMensajeEn(conversacion);
    if (vistoEn) entradas[conversacion.id] = vistoEn;
  });

  return entradas;
}

/** Lo que cabe en la burbuja: el número, o «9+» a partir del tope. */
export function textoEnBurbuja(total: number): string {
  return total > ALERTAS_MAX_EN_BURBUJA ? `${ALERTAS_MAX_EN_BURBUJA}+` : String(total);
}

/** "1 conversación con mensajes nuevos" / "3 conversaciones con mensajes nuevos". */
export function describeAlertaCount(total: number): string {
  if (total === 1) return '1 conversación con mensajes nuevos';
  return `${total} conversaciones con mensajes nuevos`;
}

/** "1 conversación esperando a una persona" / "3 conversaciones esperando a una persona". */
export function describePorAtender(total: number): string {
  if (total === 1) return '1 conversación esperando a una persona';
  return `${total} conversaciones esperando a una persona`;
}

/**
 * Etiqueta de las burbujas («3 conversaciones con mensajes nuevos sin ver»).
 *
 * Es el `aria-label` de los accesos al inbox: un número suelto no dice nada a un lector de pantalla.
 * Vacío cuando no hay nada nuevo, para que el acceso se anuncie como «Mensajes» a secas.
 */
export function describeBurbujaAlertas(total: number): string {
  if (total <= 0) return '';
  return `${describeAlertaCount(total)} sin ver`;
}

/** `title`/`aria-label` del acceso al inbox: qué hay dentro y con qué se va a encontrar. */
export function describeAccesoInbox(alertas: InboxAlertas): string {
  const partes: string[] = [alertasTexto.accesoInbox];

  if (alertas.nuevas.length > 0) partes.push(describeBurbujaAlertas(alertas.nuevas.length));
  if (alertas.porAtender > 0) partes.push(describePorAtender(alertas.porAtender));

  return partes.join(' · ');
}
